import { Contract } from '@ekt-group/general-purpose-api-interfaces';
import { Switch } from 'antd';
import { forwardRef, useImperativeHandle } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import FieldArray from '@/components/hook-form-components/FieldArray';
import InAdsWidget from '@/components/in-ads-widget/InAdsWidget';

interface ContactFormProps {
  contract: Contract;
}

const MAX_PHONE_INPUTS_COUNT = 4;
const MAX_CONTACT_EMAIL_INPUTS_COUNT = 3;
const MAX_INVOICE_EMAIL_INPUTS_COUNT = 2;

export type ContactFormValues = {
  contractNumber: string;
  companyName: string;
  emails: { value: string }[];
  invoiceEmails: { value: string }[];
  phones: { value: string }[];
  countyId?: number;
  cityId?: number;
  villageId?: number;
  street?: string;
  houseNumber?: string;
  flatNumber?: string;
  fullAddress?: string;
  invoiceSms?: boolean;
};

export interface ContactFormRef {
  getFormData: () => ContactFormValues;
}

const ContactInformationForm = forwardRef<ContactFormRef, ContactFormProps>(({ contract }, ref) => {
  const { t } = useTranslation('contractModal');

  const filteredEmails = [{ value: contract.owner?.email }, { value: contract.owner?.email2 }, { value: contract.owner?.email3 }].filter(
    (item) => item.value,
  );

  const filteredInvoiceEmails = [{ value: contract.owner?.invoiceEmail }, { value: contract.owner?.invoiceEmail2 }].filter(
    (item) => item.value,
  );

  const filteredPhones = [
    { value: contract.owner?.phone },
    { value: contract.owner?.phone2 },
    { value: contract.owner?.phone3 },
    { value: contract.owner?.phone4 },
  ].filter((item) => item.value);

  const contactForm = useForm<ContactFormValues>({
    defaultValues: {
      companyName: contract?.owner?.companyName || `${contract?.owner?.firstname} ${contract?.owner?.surname}`,
      emails: filteredEmails,
      invoiceEmails: filteredInvoiceEmails,
      phones: filteredPhones,
      invoiceSms: contract.owner?.invoiceSms,
      countyId: contract.owner?.countyId,
      cityId: contract.owner?.cityId,
      villageId: contract.owner?.villageId,
      street: contract.owner?.street,
      houseNumber: contract.owner?.houseNumber,
      flatNumber: contract.owner?.flatNumber,
      fullAddress: contract.owner?.fullAddress,
    },
  });

  useImperativeHandle(ref, () => ({
    getFormData: () => {
      return contactForm.getValues();
    },
  }));

  return (
    <FormProvider {...contactForm}>
      <form className="flex flex-col gap-4 mt-5">
        <div className="flex flex-col flex-1 gap-4">
          <span className="font-semibold text-[20px]">{t('contacts')}</span>
          <div className="flex flex-col gap-5">
            <div className="flex gap-5 sm:flex-row flex-col">
              <FieldArray
                className="flex-1"
                control={contactForm.control}
                name="emails"
                maxCount={MAX_CONTACT_EMAIL_INPUTS_COUNT}
                label={t('email')}
                placeholder={t('email')}
                appendButtonText={t('add')}
              />
              <FieldArray
                className="flex-1"
                control={contactForm.control}
                name="invoiceEmails"
                maxCount={MAX_INVOICE_EMAIL_INPUTS_COUNT}
                label={t('invoiceEmail')}
                placeholder={t('invoiceEmail')}
                appendButtonText={t('add')}
              />
            </div>
            <div className="flex flex-1 sm:gap-5 gap-2 sm:flex-row flex-col items-start">
              <FieldArray
                className="w-full sm:w-auto flex-1"
                control={contactForm.control}
                name="phones"
                maxCount={MAX_PHONE_INPUTS_COUNT}
                label={t('phone')}
                placeholder={t('phone')}
                appendButtonText={t('add')}
              />
              <div className="flex gap-2 flex-1 h-12 items-center mt-8">
                <Controller
                  name="invoiceSms"
                  control={contactForm.control}
                  render={({ field }) => <Switch className="aria-checked-[checked=true]:bg-[#D5DC2E]" {...field} />}
                />
                <span>{t('orderSmsInvoice')}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex-1 flex flex-col gap-4">
          <span className="font-semibold text-[20px]">{t('address', { ns: 'contractsTable' })}</span>
          <InAdsWidget hideMap />
        </div>
      </form>
    </FormProvider>
  );
});

export default ContactInformationForm;
